import React, { useState, useEffect, useMemo } from 'react';
import { Drawer, Button, Tabs, Form, Input, message, DatePicker, Select, Row, Col } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import DOTheme from './directOrders/doTheme';
import AttachmentsViewer from './directOrders/AttachmentsViewer';
import { getReference } from '../utils/directOrderStatus';
import { directOrderService } from '../services/directOrderService';
import lineVotesService from '../services/lineVotesService';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { API_ORIGIN } from '../config';
import { LIMIT_LABEL, totalLimitRule } from '../utils/directOrderLimits';

dayjs.extend(customParseFormat);

const DirectOrderEditModal = ({ visible, onClose, directOrder, onSave }) => {
  // Initiative dropdown options (fetched from API)
  const [initiatives, setInitiatives] = useState([]);

  // Justification dropdown options (fetched from API)
  const [justifications, setJustifications] = useState([]);
  
  // Line votes dropdown options
  const [lineVotes, setLineVotes] = useState([]);
  
  const [activeTab, setActiveTab] = useState('details');
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const mainLineVote = Form.useWatch('lineVoteId', form);

  // Active line votes, plus any on this order that were deleted since, so they still show by name
  const lineVoteOptions = useMemo(() => {
    const known = new Set(lineVotes.map((option) => option.value));
    const extra = [];
    if (directOrder?.lineVoteId && !known.has(directOrder.lineVoteId)) {
      extra.push({
        value: directOrder.lineVoteId,
        label: `${directOrder.departmentCode ?? '—'} - ${directOrder.LineVote ?? '?'} - ${directOrder.lineVoteFestival ?? ''} (deleted)`,
      });
    }
    (directOrder?.additionalLineVotes || []).forEach((lv) => {
      if (!known.has(lv.id)) {
        extra.push({ value: lv.id, label: `${lv.departmentCode ?? '—'} - ${lv.lineVote ?? '?'} - ${lv.festival ?? ''} (deleted)` });
      }
    });
    return [...lineVotes, ...extra];
  }, [lineVotes, directOrder]);

  // Fetch initiatives and justifications from API
  useEffect(() => {
    const fetchInitiatives = async () => {
      try {
        const response = await fetch(`${API_ORIGIN}/api/direct-orders/initiatives`, {
          credentials: 'include'
        });
        const data = await response.json();
        
        if (data.success && Array.isArray(data.initiatives)) {
          // Transform API response to Select options format
          const options = data.initiatives.map(item => ({
            value: String(item.id),
            label: item.initiative
          }));
          setInitiatives(options);
        }
      } catch (error) {
        message.error('Failed to load initiatives');
      }
    };
    
    const fetchJustifications = async () => {
      try {
        const response = await fetch(`${API_ORIGIN}/api/direct-orders/justifications`, {
          credentials: 'include'
        });
        const data = await response.json();
        
        if (data.success && Array.isArray(data.justifications)) {
          // Transform API response to Select options format
          // API uses justificationId, not id
          const options = data.justifications.map(item => ({
            value: String(item.justificationId),
            label: item.justification
          }));
          setJustifications(options);
        }
      } catch (error) {
        message.error('Failed to load justifications');
      }
    };
    
    const fetchLineVotes = async () => {
      try {
        const response = await lineVotesService.getAll();
        if (response.success && Array.isArray(response.lineVotes)) {
          const options = response.lineVotes
            .filter(item => item && item.id != null)
            .map(item => ({
              value: item.id,
              label: `${item.departmentCode ?? '—'} - ${item.LineVote} - ${item.Festival}`
            }));
          setLineVotes(options);
        }
      } catch (error) {
        message.error('Failed to load line votes');
      }
    };
    
    fetchInitiatives();
    fetchJustifications();
    fetchLineVotes();
  }, []);

  // Populate form with directOrder data when modal opens
  useEffect(() => {
    if (visible && directOrder) {
      // Convert initiative and justification names to IDs for form population
      let initiativeId = directOrder.doInitiative;
      let justificationId = directOrder.doJustification;
      
      // If we have names instead of IDs, convert them
      if (directOrder.doInitiativeName && !directOrder.doInitiative) {
        const foundInit = initiatives.find(item => item.label === directOrder.doInitiativeName);
        initiativeId = foundInit ? foundInit.value : undefined;
      }
      
      if (directOrder.doJustificationName && !directOrder.doJustification) {
        const foundJust = justifications.find(item => item.label === directOrder.doJustificationName);
        justificationId = foundJust ? foundJust.value : undefined;
      }
      
      // Parse contract timeframe string to date
      let contractTimeframe = undefined;
      if (directOrder.doTimeframe) {
        try {
          const date = dayjs(directOrder.doTimeframe, 'DD/MM/YYYY');
          if (date.isValid()) {
            contractTimeframe = date;
          }
        } catch (e) {
          // ignore invalid date
        }
      }
      
      form.setFieldsValue({ 
        ...directOrder,
        doInitiative: initiativeId,
        doJustification: justificationId,
        doDetailedJustification: directOrder.doDetailedJustification || '',
        doTimeframe: contractTimeframe,
        lineVoteId: directOrder.lineVoteId || undefined,
        additionalLineVoteIds: (directOrder.additionalLineVotes || []).map((lv) => lv.id),
      });
    }
  }, [visible, directOrder, form, initiatives, justifications]);

  // Save handler
  const handleSave = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();
      
      // Ensure initiative and justification are numbers (or omit if null/undefined)
      const updatePayload = {
        ...values,
        doBriefHistory: values.doBriefHistory || '',
      };
      
      // Handle initiative - convert to number or omit
      if (values.doInitiative !== null && values.doInitiative !== undefined) {
        updatePayload.doInitiative = Number(values.doInitiative);
      } else {
        delete updatePayload.doInitiative;
      }
      
      // Handle justification - convert to number or omit
      if (values.doJustification !== null && values.doJustification !== undefined) {
        updatePayload.doJustification = Number(values.doJustification);
      } else {
        delete updatePayload.doJustification;
      }
      
      // Format doTimeframe from DatePicker to string
      if (values.doTimeframe) {
        updatePayload.doTimeframe = values.doTimeframe.format('DD/MM/YYYY');
      }
      
      // Remove fields not expected by backend
      delete updatePayload.doEntityName;
      delete updatePayload.doDepartmentName;
      delete updatePayload.doChairmanName;
      delete updatePayload.doOfficerNameFull;
      
      const { doId } = directOrder;
      
      await directOrderService.updateDirectOrder(doId, updatePayload);
      message.success('Changes saved successfully!');
      
      // Call the onSave callback to refresh parent data
      if (onSave) {
        onSave(updatePayload);
      }
      
      onClose();
    } catch (err) {
      message.error(err.message || 'Failed to save changes. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!directOrder) {
    return null;
  }

  const pdfUrl = directOrder.doId ? `${API_ORIGIN}/api/direct-order/${directOrder.doId}/pdf` : null;
  const wordCount = (value) => (value ? value.trim().split(/\s+/).filter((word) => word.length > 0).length : 0);

  const detailsForm = (
    <Form form={form} layout="vertical" requiredMark={false}>
      <h3 className="do-form-heading" style={{ marginTop: 0 }}>Request</h3>
      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <Form.Item label="Date" name="doDate">
            <Input type="date" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item label="Entity and department" name="doEntityName">
            <Input disabled />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item label="Initiative" name="doInitiative">
            <Select options={initiatives} showSearch optionFilterProp="label" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item label="Line vote" name="lineVoteId">
            <Select
              options={lineVoteOptions}
              placeholder="Select a line vote"
              showSearch
              optionFilterProp="label"
              allowClear
              onChange={(value) => {
                // The main line vote can't also be an additional one
                const additional = form.getFieldValue('additionalLineVoteIds') || [];
                form.setFieldsValue({ additionalLineVoteIds: additional.filter((id) => id !== value) });
              }}
            />
          </Form.Item>
        </Col>
        <Col xs={24}>
          <Form.Item label="Additional line votes" name="additionalLineVoteIds">
            <Select
              mode="multiple"
              options={lineVoteOptions.filter((option) => option.value !== mainLineVote)}
              placeholder="Select any other line votes"
              showSearch
              optionFilterProp="label"
              allowClear
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item label="Officer raising the request" name="doOfficerNameFull">
            <Input disabled />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item label="Perm Sec / Chairman approving" name="doChairmanName">
            <Input disabled />
          </Form.Item>
        </Col>
      </Row>

      <h3 className="do-form-heading">Justification</h3>
      <Form.Item label="Justification (procurement policy note 32, section 1)" name="doJustification">
        <Select options={justifications} showSearch optionFilterProp="label" />
      </Form.Item>
      <Form.Item
        label="Detailed justification"
        name="doDetailedJustification"
        rules={[
          { required: true, message: 'Detailed justification is required' },
          {
            validator: (_, value) => {
              const words = wordCount(value);
              return words > 500
                ? Promise.reject(new Error(`Maximum 500 words allowed (current: ${words} words)`))
                : Promise.resolve();
            },
          },
        ]}
      >
        <Input.TextArea
          rows={4}
          placeholder="Explain why this has to be a direct order (500 words at most)"
          showCount={{ formatter: ({ value }) => `${wordCount(value)} / 500 words` }}
        />
      </Form.Item>

      <h3 className="do-form-heading">Supplier and items</h3>
      <Form.Item label="Supplier" name="doSupplier">
        <Input />
      </Form.Item>
      <Form.Item label="Name and brief description" name="doNameBriefDesc">
        <Input.TextArea rows={2} />
      </Form.Item>
      <Form.Item label="Breakdown of works, supplies or services" name="doBreakdown">
        <Input.TextArea rows={2} />
      </Form.Item>
      <Row gutter={16}>
        <Col xs={24} sm={8}>
          <Form.Item label="Contract time frame" name="doTimeframe">
            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" placeholder="Select a date" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={8}>
          <Form.Item label="Cost per item or rate (excl. VAT)" name="doCostPerItem">
            <Input />
          </Form.Item>
        </Col>
        <Col xs={24} sm={8}>
          <Form.Item
            label="Total cost (excl. VAT)"
            name="doTotalCost"
            extra={`Up to ${LIMIT_LABEL}`}
            rules={[{ required: true, message: 'Total cost is required' }, totalLimitRule]}
          >
            <Input />
          </Form.Item>
        </Col>
      </Row>

      <h3 className="do-form-heading">Procurement and funding</h3>
      <Form.Item label="Call for quotation or expression of interest" name="doCallForQuiteOrInterest">
        <Input />
      </Form.Item>
      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <Form.Item label="Terms of payment" name="doTermsOfPayment">
            <Input />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item label="Reference and date of winning bid, contract or offer" name="doRefNoAndDateOfWiningBidEtc">
            <Input />
          </Form.Item>
        </Col>
      </Row>
      <Form.Item label="Availability and source of funds" name="doFundsAvailability">
        <Input />
      </Form.Item>
      <Form.Item label="Two-year history with this supplier" name="doBriefHistory">
        <Input.TextArea rows={2} />
      </Form.Item>
    </Form>
  );

  const tabItems = [
    { key: 'details', label: 'Details', children: detailsForm },
    ...(pdfUrl
      ? [
          {
            key: 'attachments',
            label: 'Attachments',
            children: (
              <AttachmentsViewer doId={directOrder.doId} attachments={directOrder.doAttachments} title="Attachments" />
            ),
          },
        ]
      : []),
  ];

  return (
    <DOTheme>
      <Drawer
        open={visible}
        onClose={onClose}
        width={760}
        rootClassName="do-root do-scope"
        title={
          <span style={{ fontWeight: 600 }}>
            Edit direct order <span className="do-ref">{getReference(directOrder)}</span>
          </span>
        }
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button onClick={onClose}>Cancel</Button>
            <Button type="primary" icon={<SaveOutlined />} loading={loading} onClick={handleSave}>
              Save and resubmit
            </Button>
          </div>
        }
        destroyOnClose
      >
        <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
      </Drawer>
    </DOTheme>
  );
};

export default DirectOrderEditModal;
